
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './talentmarketplace.css';
import heroImage from '../assets/images/hire-talent-hero.webp';
import { getTalents } from '../data/talent-data';
import { fetchPublishedGraduates } from '../utils/graduatesApi';
import { mergeTalents } from '../utils/talentHelpers';

const TalentPhoto = ({ talent }) => {
    const [imgSrc, setImgSrc] = useState(talent.image);

    return (
        <img
            src={imgSrc}
            alt={`${talent.name} - ${talent.title}`}
            loading="lazy"
            onError={() => setImgSrc(talent.fallbackImage)}
        />
    );
};

const TalentMarketplace = () => {
    const [talents, setTalents] = useState([]);
    const [filteredTalents, setFilteredTalents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [apiCount, setApiCount] = useState(0);

    const featuredTalents = useMemo(
        () => talents.filter((t) => t.availability === 'Available').slice(0, 3),
        [talents]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            const staticTalents = getTalents();
            const apiGraduates = await fetchPublishedGraduates();
            const merged = mergeTalents(staticTalents, apiGraduates);

            if (!cancelled) {
                setTalents(merged);
                setFilteredTalents(merged);
                setApiCount(apiGraduates.length);
                setIsLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (featuredTalents.length === 0) return undefined;
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % featuredTalents.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [featuredTalents.length]);

    useEffect(() => {
        let filtered = talents;

        if (searchTerm) {
            filtered = filtered.filter(talent =>
                talent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                talent.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                talent.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(talent =>
                talent.skills.some(skill => skill.toLowerCase().includes(selectedCategory.toLowerCase())) ||
                talent.title.toLowerCase().includes(selectedCategory.toLowerCase())
            );
        }

        setFilteredTalents(filtered);
    }, [searchTerm, selectedCategory, talents]);

    const categories = [
        { id: 'all', name: 'All Skills' },
        { id: 'react', name: 'React Development' },
        { id: 'node', name: 'Node.js Development' },
        { id: 'javascript', name: 'JavaScript' },
        { id: 'mongodb', name: 'MongoDB' },
        { id: 'security', name: 'Cybersecurity' },
        { id: 'fullstack', name: 'Fullstack Development' },
        { id: 'hacking', name: 'Ethical Hacking' },
    ];

    return (
        <div className="talent-marketplace">
            <section className="talent-hero py-5" style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${heroImage})`,
            }}>
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="hero-content">
                                <span className="subheading" style={{ color: '#ffd700' }}>Hire Top Tech Talent</span>
                                <h1 className="display-4 fw-bold mb-3">Find Skilled Graduates from Abia Tech Hub Academy</h1>
                                <p className="lead mb-4">
                                    Connect with our verified graduates who have completed industry-leading programs.
                                    Get access to pre-screened, job-ready talent across various tech disciplines.
                                </p>
                                <div className="stats-container mb-4">
                                    <div className="row">
                                        <div className="col-4">
                                            <div className="stat-item">
                                                <h3 className="fw-bold">{talents.length || '45+'}</h3>
                                                <p>Verified Talents</p>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="stat-item">
                                                <h3 className="fw-bold">100%</h3>
                                                <p>Success Rate</p>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="stat-item">
                                                <h3 className="fw-bold">24hrs</h3>
                                                <p>Response Time</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="btn-container d-flex gap-3">
                                    <Link to="/company-register" className="btn rounded-pill px-4 py-2" style={{
                                        backgroundColor: '#8A038C',
                                        color: 'white',
                                        border: '1px solid #8A038C',
                                        minWidth: '160px',
                                        textAlign: 'center',
                                    }}>
                                        Start Hiring
                                    </Link>
                                    <Link to="/talent-program" className="btn rounded-pill px-4 py-2" style={{
                                        backgroundColor: 'transparent',
                                        color: '#8A038C',
                                        border: '2px solid #8A038C',
                                        minWidth: '160px',
                                        textAlign: 'center',
                                    }}>
                                        Learn More
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-image">
                                <div className="talent-showcase">
                                    <div className="talent-slideshow">
                                        {featuredTalents.map((talent, index) => (
                                            <Link
                                                key={talent.id}
                                                to={talent.profilePath}
                                                className={`talent-card featured ${currentSlide === index ? 'active' : ''}`}
                                                style={{ display: currentSlide === index ? 'flex' : 'none', textDecoration: 'none' }}
                                            >
                                                <div className="talent-image">
                                                    <TalentPhoto talent={talent} />
                                                </div>
                                                <div className="talent-info">
                                                    <h5>{talent.name.split(' ').slice(0, 2).join(' ')}</h5>
                                                    <p>{talent.title}</p>
                                                    <div className="verified-section">
                                                        <i className="fas fa-check-circle"></i>
                                                        <span>Verified Graduate</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                    <div className="slideshow-indicators">
                                        {featuredTalents.map((_, index) => (
                                            <span key={index} className={`indicator ${currentSlide === index ? 'active' : ''}`}></span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="search-filter-section py-4 bg-light">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="search-box">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="fas fa-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search talents by name, skills, or role..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="category-filters">
                                <select
                                    className="form-select"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {apiCount > 0 && (
                        <p className="text-muted small mt-2 mb-0">
                            Showing marketplace graduates plus {apiCount} published profile{apiCount !== 1 ? 's' : ''} from Super Admin.
                        </p>
                    )}
                </div>
            </section>

            <section className="talents-section py-5">
                <div className="container">
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <h2 className="mb-0">Available Talents</h2>
                                <span className="text-muted">{filteredTalents.length} talents found</span>
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: '#8A038C' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading talents...</p>
                        </div>
                    ) : (
                        <div className="row g-4">
                            {filteredTalents.map(talent => (
                                <div className="col-lg-6" key={talent.id}>
                                    <div className="talent-card">
                                        <div className="talent-card-layout">
                                            <Link to={talent.profilePath} className="talent-photo-link">
                                                <div className="talent-photo">
                                                    <TalentPhoto talent={talent} />
                                                    {talent.verified && (
                                                        <span className="photo-verified-badge" title="Verified Graduate">
                                                            <i className="fas fa-check"></i>
                                                        </span>
                                                    )}
                                                    <span className={`photo-status-badge ${talent.availability.toLowerCase()}`}>
                                                        {talent.availability}
                                                    </span>
                                                </div>
                                            </Link>

                                            <div className="talent-details">
                                                <h4 className="talent-name">
                                                    <Link to={talent.profilePath} className="talent-name-link">{talent.name}</Link>
                                                </h4>
                                                <p className="talent-title">{talent.title}</p>
                                                <p className="talent-description">{talent.description}</p>

                                                <div className="talent-skills">
                                                    {talent.skills.slice(0, 4).map((skill, index) => (
                                                        <span key={index} className="skill-tag">{skill}</span>
                                                    ))}
                                                    {talent.skills.length > 4 && (
                                                        <span className="skill-tag more">+{talent.skills.length - 4}</span>
                                                    )}
                                                </div>

                                                <div className="talent-meta">
                                                    <div className="meta-item">
                                                        <i className="fas fa-map-marker-alt"></i>
                                                        <span>{talent.location}</span>
                                                    </div>
                                                    <div className="meta-item">
                                                        <i className="fas fa-graduation-cap"></i>
                                                        <span>{talent.experience}</span>
                                                    </div>
                                                </div>

                                                <Link to={talent.profilePath} className="view-profile-link">
                                                    View profile <i className="fas fa-arrow-right ms-1"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!isLoading && filteredTalents.length === 0 && (
                        <div className="text-center py-5">
                            <i className="fas fa-search fa-3x text-muted mb-3"></i>
                            <h4>No talents found</h4>
                            <p className="text-muted">Try adjusting your search criteria or browse all talents.</p>
                        </div>
                    )}
                </div>
            </section>

            <section className="cta-section py-5" style={{ backgroundColor: '#8A038C' }}>
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-8">
                            <h2 className="text-white mb-3">Ready to Hire Top Tech Talent?</h2>
                            <p className="text-white-50 mb-0">
                                Join leading companies who trust Abia Tech Hub Academy graduates.
                                Get access to our verified talent pool and start building your dream team today.
                            </p>
                        </div>
                        <div className="col-lg-4 text-end">
                            <Link to="/company-register" className="btn btn-light btn-lg rounded-pill px-4">
                                Get Started Now <i className="fas fa-arrow-right ms-2"></i>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TalentMarketplace;
